import { useMemo } from "react";
import Table, { CommonTableData } from "./ImpersonationFilterTable/ImpersonationFilterTable";
import { ReactComponent as IconExportDelete } from "../../../../../common/icons/IconExportDelete/svg/icon_export_delete.svg";
import { IconMobilePhone } from "../../../../../common/icons/IconMobilePhone/IconMobilePhone";
import { IconWorld } from "../../../../../common/icons/IconWorld/IconWorld";
import { IconAcademy } from "../../../../../common/icons/IconAcademy/IconAcademy";
import { ImpersonationDetails, ImpersonationType } from "../../../../../common/interfaces/impersonation";
import { getCountryFlag, getCountryFlagByAff } from "../../../../../common/util/getCountryFlag";
import { Markets } from "../../../../Lit/acc-es-data/model";
import { affiliates } from "../../../impersonation-util";
import "./ImpersonationTable.scss";
import useWindowSize from "../../../../../common/hooks/useWindowSize";
import { Button } from "../../../../../common/components/Button/Button";

type Actions = {
  impersonateWithSelectedABO: Function;
  impersonateWithAmywayPlus: Function;
  impersonateWithAcademy: Function;
  remove: Function;
};

export type ImpersonationTableProps = {
  impersonationHistory: ImpersonationDetails[];
  actions: Actions;
};

const PLATFORM_ICON: Record<ImpersonationType, JSX.Element> = {
  amwayPlus: <IconMobilePhone fill="white" />,
  myBizWeb: <IconWorld fill="white" />,
  academyWeb: <IconAcademy color="white" />,
  entra: <IconWorld fill="white" />,
  default: <IconWorld fill="white" />,
};

const createAction = (impersonation: ImpersonationDetails, actions: Actions) => {
  const impersonationButton = () => {
    let onClick: () => void;

    if (impersonation.impersonationType === "amwayPlus") {
      onClick = () => actions.impersonateWithAmywayPlus(impersonation);
    } else if (impersonation.impersonationType === "academyWeb") {
      onClick = () => actions.impersonateWithAcademy(impersonation);
    } else {
      onClick = () => actions.impersonateWithSelectedABO(impersonation);
    }

    return (
      <Button
        type="button"
        className="ImpersonationTable__impersonateButton"
        data-testid="impersonation-history-impersonate"
        onClick={onClick}
      >
        {PLATFORM_ICON[impersonation.impersonationType]}
      </Button>
    );
  };

  return (
    <div className="ImpersonationTable__rowItem ImpersonationTable__buttonGroup">
      <Button
        type="button"
        className="ImpersonationTable__deleteButton"
        data-testid="impersonation-history-delete"
        onClick={() => actions.remove(impersonation)}
      >
        <IconExportDelete color="white" />
      </Button>

      {impersonationButton()}
    </div>
  );
};

const PLATFORM_LABEL: Record<ImpersonationType, string> = {
  myBizWeb: "MyBiz Web",
  amwayPlus: "Native",
  academyWeb: "Academy Web",
  entra: "Entra",
  default: "MyBiz Web",
};

const createCountry = (impersonation: ImpersonationDetails) => {
  const country = new Markets().getByCtry(impersonation.country);
  let isoCountryCode = "US";
  if (country) {
    isoCountryCode = country.iso;
  }

  return (
    <span className="ImpersonationTable__rowItem">
      <span>{getCountryFlag(isoCountryCode)}</span>
      <span>{impersonation.country}</span>
    </span>
  );
};

const createTableData = (impersonationHistory: ImpersonationDetails[], actions: Actions): CommonTableData[] => {
  const tableData: CommonTableData[] = [];

  impersonationHistory.forEach((impersonation) => {
    tableData.push({
      abo: {
        searchKey: impersonation.abo,
        displayValue: impersonation.abo,
      },
      country: {
        searchKey: impersonation.country,
        displayValue: createCountry(impersonation),
      },
      partyId: {
        searchKey: impersonation.partyId ?? "",
        displayValue: impersonation.partyId ?? "",
      },
      platform: {
        searchKey: PLATFORM_LABEL[impersonation.impersonationType] ?? impersonation.impersonationType,
        displayValue: PLATFORM_LABEL[impersonation.impersonationType] ?? impersonation.impersonationType,
      },
      note: {
        searchKey: impersonation.note ?? "",
        displayValue: impersonation.note || "-",
      },
      actions: {
        searchKey: impersonation.abo,
        displayValue: createAction(impersonation, actions),
      },
    });
  });
  return tableData;
};

// Only used to drive the desktop <table> columns and the search-match scope
// (mobile cards render their own fixed layout via MOBILE_CARD_ROWS below, so
// every field — including "note" — must stay here for mobile search to work).
const getHeader = () => [
  { label: "ABO", accessor: "abo" },
  { label: "Country", accessor: "country" },
  { label: "Platform", accessor: "platform" },
  { label: "Notes", accessor: "note" },
  { label: "", accessor: "actions" },
];

const MOBILE_CARD_ROWS: { label: string; accessor: string }[] = [
  { label: "ABO", accessor: "abo" },
  { label: "Country", accessor: "country" },
  { label: "Platform", accessor: "platform" },
  { label: "Notes", accessor: "note" },
];

const renderMobileCard = (row: CommonTableData, index: number) => (
  <div className="ImpersonationTable__mobileCard" key={index}>
    {MOBILE_CARD_ROWS.map(({ label, accessor }) => (
      <div className="ImpersonationTable__mobileCardRow" key={accessor}>
        <span className="ImpersonationTable__mobileCardLabel">{label}</span>
        <span className="ImpersonationTable__mobileCardValue">{row[accessor]?.displayValue}</span>
      </div>
    ))}
    <div className="ImpersonationTable__mobileCardRow">
      <span className="ImpersonationTable__mobileCardLabel">Actions</span>
      <span className="ImpersonationTable__mobileCardValue">{row.actions?.displayValue}</span>
    </div>
  </div>
);

export const ImpersonationTable = ({ impersonationHistory, actions }: ImpersonationTableProps) => {
  const { width } = useWindowSize();
  const isDesktop = !!(width && width > 600);

  const tableHeader = getHeader();
  const tableData = createTableData(impersonationHistory, actions);
  const countryCodes = useMemo(
    () => [...new Set(affiliates.map((aff) => aff.country))].sort((a, b) => a.localeCompare(b)),
    [],
  );

  const countryCodesFilterTags = useMemo(
    () =>
      countryCodes.map((country) => ({
        filterKey: country,
        displayValue: (
          <span className="ImpersonationTable__filterTagItem">
            <span>{getCountryFlagByAff(country)}</span>
            <span>{country}</span>
          </span>
        ),
      })),
    [countryCodes],
  );

  return (
    <div className="ImpersonationTable">
      <Table
        header={tableHeader}
        data={tableData}
        enableFilter={true}
        enableSearch={true}
        filterMenuData={{ accessor: "country", filterTags: countryCodesFilterTags }}
        renderMobileCard={isDesktop ? undefined : renderMobileCard}
      />
    </div>
  );
};
